import React from 'react'
import { useSelector } from 'react-redux'

import { RouteKey } from '@src/store/constants'

import { PlayerState, RootStateTypeExtra } from '@store/types'

import { checkGalleryTab } from '@utilities/floor-tab'

import useDetectInactivity from '@adUtilities/hooks/use-detect-inactivity'

interface UseVideoScreensaverArgs {
  enabled: boolean
  delay?: number
  videoUrl?: string
}

const useVideoScreensaver = ({
  enabled,
  delay,
  videoUrl,
}: UseVideoScreensaverArgs) => {
  const { session, connectedToSession, activeRoute } = useSelector(
    (state: RootStateTypeExtra) => ({
      session: state.firestore?.session,
      connectedToSession: !!state.firestore?.session?.connected,
      activeRoute: state.firestore?.session?.activeRoute,
    })
  )
  const { slideshowIsPlaying, videoIsPlaying } = useSelector(
    (state: RootStateTypeExtra) => ({
      slideshowIsPlaying: state.appConfig.slideshowIsPlaying,
      videoIsPlaying: state.appConfig.videoIsPlaying,
    })
  )

  const { isGalleryTab, hasActiveProperty } = useSelector(
    (state: RootStateTypeExtra) => {
      const { building, houseAndLand, floorGallery } =
        state.firestore?.session || {}
      return {
        isGalleryTab: floorGallery?.galleryControlV2?.activeTabIndex
          ? checkGalleryTab(floorGallery?.galleryControlV2?.activeTabIndex)
          : false,
        hasActiveProperty: !!(
          building?.activeUnit || houseAndLand?.activeLotId
        ),
      }
    }
  )

  const {
    propertyGallerySlideshowIsPlayingInSession,
    gallerySlideshowIsPlayingInSession,
  } = useSelector((state: RootStateTypeExtra) => {
    const { propertyGallery, design } = state.firestore?.session || {}
    return {
      propertyGallerySlideshowIsPlayingInSession:
        !!propertyGallery?.galleryControlV2?.autoPlayState,
      gallerySlideshowIsPlayingInSession:
        !!design?.galleryControlV2?.autoPlayState,
    }
  })

  const {
    floorGalleryVideoIsPlayingInSession,
    propertyGalleryVideoIsPlayingInSession,
    videoGalleryVideoIsPlayingInSession,
  } = useSelector((state: RootStateTypeExtra) => {
    const { floorGallery, propertyGallery, videoGallery } =
      state.firestore?.session || {}
    return {
      floorGalleryVideoIsPlayingInSession:
        floorGallery?.playerControlV2?.playerState !== PlayerState.Stop,
      propertyGalleryVideoIsPlayingInSession:
        propertyGallery?.playerControlV2?.playerState !== PlayerState.Stop,
      videoGalleryVideoIsPlayingInSession:
        videoGallery?.playerControlV2?.playerState !== PlayerState.Stop,
    }
  })

  const slideshowIsPlayingInSession = React.useMemo(() => {
    const isPropertyRoute =
      activeRoute === RouteKey.BUILDING || activeRoute === RouteKey.STAGES
    if (!connectedToSession) {
      return false
    }
    if (
      propertyGallerySlideshowIsPlayingInSession &&
      isPropertyRoute &&
      hasActiveProperty
    ) {
      return true
    }
    if (
      gallerySlideshowIsPlayingInSession &&
      activeRoute === RouteKey.GALLERY
    ) {
      return true
    }
    return false
  }, [
    connectedToSession,
    activeRoute,
    hasActiveProperty,
    propertyGallerySlideshowIsPlayingInSession,
    gallerySlideshowIsPlayingInSession,
  ])

  const videoIsPlayingInSession = React.useMemo(() => {
    const isPropertyRoute =
      activeRoute === RouteKey.BUILDING || activeRoute === RouteKey.STAGES

    if (!connectedToSession) {
      return false
    }
    if (
      floorGalleryVideoIsPlayingInSession &&
      isPropertyRoute &&
      isGalleryTab
    ) {
      return true
    }
    if (
      propertyGalleryVideoIsPlayingInSession &&
      isPropertyRoute &&
      hasActiveProperty
    ) {
      return true
    }
    if (
      videoGalleryVideoIsPlayingInSession &&
      activeRoute === RouteKey.VIDEO_GALLERY
    ) {
      return true
    }
    return false
  }, [
    connectedToSession,
    activeRoute,
    isGalleryTab,
    hasActiveProperty,
    floorGalleryVideoIsPlayingInSession,
    propertyGalleryVideoIsPlayingInSession,
    videoGalleryVideoIsPlayingInSession,
  ])

  const isInvalid = !enabled || !videoUrl || delay === undefined
  const skipScreensaver =
    slideshowIsPlayingInSession ||
    videoIsPlayingInSession ||
    slideshowIsPlaying ||
    videoIsPlaying

  const canShowScreensaver = React.useMemo<boolean>(() => {
    if (isInvalid || skipScreensaver) {
      return false
    }
    return true
  }, [isInvalid, skipScreensaver])

  const { isInactive, reset } = useDetectInactivity({
    disable: !canShowScreensaver,
    timeoutInSeconds: delay,
  })

  const showScreensaver = canShowScreensaver && isInactive

  React.useEffect(() => {
    if (!canShowScreensaver) {
      return
    }
    reset()
  }, [session])

  return React.useMemo(
    () => ({
      showScreensaver,
    }),
    [showScreensaver]
  )
}

export default useVideoScreensaver
